import { api } from './api';

export interface Booking {
  _id: string;
  userId?: string;
  schoolId: string;
  courseId?: string;
  schoolName?: string;
  courseTitle?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  substatus?: 'pending payment' | 'pending approval from school';
  name: string;
  email: string;
  phone?: string;
  message?: string;
  selectedMonth?: string;
  paymentLink?: string;
  actionTimeline?: Array<{
    action: string;
    substatus?: 'pending payment' | 'pending approval from school';
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    performedBy: 'USER' | 'SCHOOL' | 'SYSTEM';
    performedAt: string;
    notes?: string;
  }>;
  notes?: string;
  submittedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  paymentCompletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BookingStats {
  totalBookings: number;
  approvedBookings: number;
  rejectedBookings: number;
  cancelledBookings: number;
  pendingBookings: number;
  pendingPaymentBookings: number;
  pendingApprovalBookings: number;
  recentBookings: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  bookings: T[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

class BookingService {
  // Get all bookings with pagination and filters
  async getBookings(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    substatus?: string;
    schoolId?: string;
  } = {}): Promise<ApiResponse<PaginatedResponse<Booking>>> {
    try {
      // Default values
      const requestBody = {
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search || undefined,
        status: params.status && params.status !== 'ALL' ? params.status : undefined,
        substatus: params.substatus && params.substatus !== 'ALL' ? params.substatus : undefined,
        schoolId: params.schoolId || undefined,
      };
      
      // Remove undefined values
      Object.keys(requestBody).forEach(key => 
        requestBody[key as keyof typeof requestBody] === undefined && delete requestBody[key as keyof typeof requestBody]
      );
      
      const response = await api.post('/bookings', requestBody);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  }

  // Get booking by ID
  async getBookingById(id: string): Promise<ApiResponse<Booking>> {
    try {
      const response = await api.get(`/bookings/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  }

  // Approve booking
  async approveBooking(
    id: string, 
    notes?: string
  ): Promise<ApiResponse<Booking>> {
    try {
      const response = await api.post(`/bookings/${id}/approve`, {
        notes
      });
      return response.data;
    } catch (error: any) {
      console.error('Error approving booking:', error);
      throw error;
    }
  }

  // Reject booking
  async rejectBooking(
    id: string, 
    notes?: string
  ): Promise<ApiResponse<Booking>> {
    try {
      const response = await api.post(`/bookings/${id}/reject`, {
        notes
      });
      return response.data;
    } catch (error: any) {
      console.error('Error rejecting booking:', error);
      throw error;
    }
  }

  // Update booking
  async updateBooking(
    id: string, 
    data: Partial<Booking>
  ): Promise<ApiResponse<Booking>> {
    try {
      const response = await api.put(`/bookings/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating booking:', error);
      throw error;
    }
  }

  // Delete booking
  async deleteBooking(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/bookings/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  }

  // Get booking statistics
  async getBookingStats(): Promise<ApiResponse<{ stats: BookingStats }>> {
    try {
      const response = await api.get('/bookings/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching booking stats:', error);
      throw error;
    }
  }

  // Utility function to format booking status
  formatStatus(status: Booking['status']): string {
    const statusMap = {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled'
    };
    return statusMap[status] || status;
  }

  // Utility function to format substatus
  formatSubstatus(substatus?: Booking['substatus']): string {
    if (!substatus) return '';
    const statusMap = {
      'pending approval from school': 'Pending Approval',
      'pending payment': 'Pending Payment'
    };
    return statusMap[substatus] || substatus;
  }

  // Utility function to get status color
  getStatusColor(status: Booking['status']): string {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Utility function to get substatus color
  getSubstatusColor(substatus?: Booking['substatus']): string {
    if (!substatus) return 'bg-gray-100 text-gray-800';
    const colorMap = {
      'pending approval from school': 'bg-yellow-100 text-yellow-800',
      'pending payment': 'bg-orange-100 text-orange-800'
    };
    return colorMap[substatus] || 'bg-gray-100 text-gray-800';
  }

  // Utility function to format date
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Utility function to format relative time
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    }
  }
}

export const bookingService = new BookingService();
export default bookingService;




